import React, { memo } from 'react';
import { useController } from 'react-hook-form';

import DateRangeController from '../Fields/FieldDateRangeController/DateRangeController';

/**
 * ControlledDateRangeController bridges the existing react-dates based
 * `DateRangeController` (a `DayPickerRangeController` wrapper) to react-hook-form.
 *
 * It is the RHF replacement for the retired Final-Form `FieldDateRangeController`
 * and preserves the exact value shape that BookingDateRangeFilter relies on:
 * the form field (`name`, e.g. "dates") holds `{ startDate: Date, endDate: Date }`
 * once both dates are selected (or is empty/undefined otherwise). The underlying
 * controller only emits onChange when BOTH dates are present, matching the prior
 * Final-Form behavior.
 *
 * `controllerRef` is forwarded to the underlying DateRangeController instance so
 * callers can imperatively call `.onReset(startDate, endDate)` for clear/cancel,
 * exactly as before. Clearing/cancelling the visible calendar via onReset does
 * not by itself rewrite form state — the parent FilterForm/FilterPopup handlers
 * own submitting the cleared/cancelled value, preserving the original flow.
 *
 * @param {object} props
 * @param {string} props.name - The name of the form field. Required.
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form. Required.
 * @param {Function} [props.controllerRef] - Ref callback receiving the DateRangeController instance.
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name' | 'control'>} [props.controllerOptions] - Options for the controller.
 */
const ControlledDateRangeControllerComponent = ({
  name,
  control,
  controllerRef,
  controllerOptions = {},
  ...rest
}) => {
  if (!name) {
    throw new Error('ControlledDateRangeController: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledDateRangeController: control is required');
  }

  const { field } = useController({
    name,
    control,
    ...controllerOptions,
  });

  return (
    <DateRangeController
      {...rest}
      onChange={field.onChange}
      ref={controllerRef}
      value={field.value}
    />
  );
};

ControlledDateRangeControllerComponent.displayName = 'ControlledDateRangeController';

const ControlledDateRangeController = memo(ControlledDateRangeControllerComponent);

export default ControlledDateRangeController;
